import { useCallback, useEffect, ReactElement } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { components, GroupBase, OptionProps } from 'react-select';
import Creatable, { CreatableProps } from 'react-select/creatable';
import {
  withAsyncPaginate,
  UseAsyncPaginateParams,
  ComponentProps,
} from 'react-select-async-paginate';
import { FaPlus } from "react-icons/fa"
import { ClienteView, useLazyGetClientesQuery } from '../services/clientesApi';
import { ClienteFormModal } from './ClienteFormModal';

const OptionWithDescription = <
  IsMulti extends boolean,
  Group extends GroupBase<ClienteView>
>({...props}: OptionProps<ClienteView, IsMulti, Group>)=>{
  if((props.data as any).__isNew__) return <components.Option {...props}/>
  return <components.Option {...props}>
    <div>{props.label}</div>
    <div className={props.isSelected ? "text-light" : "text-muted"}>{(props.selectProps as any).getOptionSubLabel(props.data)}</div>
  </components.Option>
}

type Props<OptionType, IsMulti extends boolean, Group extends GroupBase<OptionType>> = CreatableProps<OptionType, IsMulti, Group>
& UseAsyncPaginateParams<OptionType, Group, {page: number}>
& ComponentProps<OptionType, Group, IsMulti>;

const Select = withAsyncPaginate(Creatable) as (<
  OptionType,
  IsMulti extends boolean,
  Group extends GroupBase<OptionType>>(props: Props<OptionType, IsMulti, Group>) => ReactElement)

type CustomProps = {
  feedback?: string
}


export function ClienteSelect<IsMulti extends boolean, Group extends GroupBase<ClienteView>>({
  feedback,
  ...props
}: Omit<Props<ClienteView, IsMulti, Group>, "loadOptions"> & CustomProps){
  const [buscar, buscarState] = useLazyGetClientesQuery()

  const modalActions = ClienteFormModal.useActions()

  const loadOptions = useCallback<Props<ClienteView, IsMulti, Group>["loadOptions"]>(async (search, loadedOptions, additional)=>{
    if(!search) return { options: [] }
    const response = await buscar({
      search,
      page: {
        current: additional!.page,
        size: 100
      }
    }).unwrap()

    return {
      options: response.records,//[...loadedOptions, ...response.records],
      hasMore: !!response.meta.nextPage,
      additional: response.meta.nextPage !== undefined ? { page: response.meta.nextPage } : undefined
    }
  }, [buscarState])

  useEffect(()=>{
    return ()=>modalActions.close()
  }, [])

  let className = "react-select-container"
  if(props.className) className += " " + props.className
  
  return <InputGroup hasValidation>
    <Select
      //@ts-ignore
      getOptionSubLabel={props.getOptionSubLabel ?? (data => `${data.documentoIdentidad.tipoText}: ${data.documentoIdentidad.numero}`)}
      isClearable
      {...props}
      className={["form-control", props.className].filter(cn=>!!cn).join(" ")}
      styles={{
        menuPortal: base => ({ ...base, zIndex: 999 }),
        container: base => ({...base, padding: 0, flex: 1}),
        control: (styles: any)=>{
          delete styles["boxShadow"]
          return {
            ...styles,
            borderRadius: "0.375rem",
            borderWidth: 0,
          }
        },
        ...(props.styles ?? {})
      }}
      components={{
        Option: OptionWithDescription
      }}
      menuPlacement="auto"
      placeholder=""
      isValidNewOption={(inputValue)=>{
        return !!inputValue && !buscarState.data?.meta.nextPage && !buscarState.isUninitialized
      }}
      formatCreateLabel={()=><b>
        <FaPlus className="me-2" />
        <em>Registrar Nuevo</em>
      </b>}
      getOptionValue={option => (option as any).value ?? String(option.id)}
      getOptionLabel={option => (option as any).label ?? `${option.id}, ${option.nombreCompleto}`}
      loadOptionsOnMenuOpen={false}
      debounceTimeout={300}
      additional={{page: 1}}
      loadOptions={loadOptions}
      menuPortalTarget={document.body}
      onCreateOption={()=>{
        console.log("create")
        modalActions.open({
          onCreated: (cliente) => {
            //@ts-ignore
            props.onChange?.(props.isMulti ? [cliente] : cliente, {
              action: "create-option"
            })
            modalActions.close()
          }
        })
      }}
    />
    {/* {buscarState.isError ? <Button><FaSync /></Button> : null} */}
    <Form.Control.Feedback type="invalid">{feedback}</Form.Control.Feedback>
  </InputGroup>
}